import React from "react";
import { Check, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";

export const fieldCls = "h-10 w-full rounded-[10px] border border-black/[0.1] bg-white px-3 text-[14px] outline-none transition-[border-color,box-shadow] placeholder:text-muted-foreground/60 focus:border-primary/60 focus:ring-2 focus:ring-primary/15 aria-[invalid=true]:border-[hsl(var(--v-blocked))] disabled:bg-black/[0.02] disabled:text-muted-foreground";
export const labelCls = "text-[12.5px] font-medium text-foreground/70";
export const primaryBtn = "inline-flex h-10 items-center justify-center gap-2 rounded-full bg-[hsl(var(--navy))] px-5 text-[13.5px] font-semibold text-white transition-colors hover:bg-[hsl(var(--ink-2))] disabled:opacity-40";
export const quietBtn = "inline-flex h-10 items-center justify-center gap-2 rounded-full border border-black/[0.1] bg-white px-4 text-[13.5px] font-medium text-foreground/80 transition-colors hover:border-black/[0.25] hover:text-foreground disabled:opacity-40";
export const ghostBtn = "inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium text-foreground/70 transition-colors hover:bg-black/[0.05] hover:text-foreground disabled:opacity-40";

export function Field({ label, htmlFor, hint, error, children, className }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      {label ? <label htmlFor={htmlFor} className={labelCls}>{label}</label> : null}
      {children}
      {error ? <p className="text-[12px] text-[hsl(var(--v-blocked))]" role="alert">{error}</p> : hint ? <p className="text-[12px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export function SheetSection({ title, aside, children, testId, className }) {
  return (
    <section className={cn("px-6 py-5", className)} data-testid={testId}>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-[13px] font-semibold tracking-[-0.005em] text-foreground">{title}</h3>
        {aside ? <div className="text-[12px] text-muted-foreground">{aside}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function DeptSelect({ value, onChange, departments, id, testId, disabled }) {
  return (
    <select id={id} value={value || ""} onChange={(e) => onChange(e.target.value || null)} disabled={disabled} className={cn(fieldCls, "appearance-auto pr-2")} data-testid={testId}>
      <option value="">Ingen avdeling</option>
      {(departments || []).map((d) => <option key={d.id} value={d.code}>{d.code} {d.name}</option>)}
    </select>
  );
}

/** Four roles as quiet choice tiles – the role is a description of the job; the note says what it does (and does not) change. */
export function RolePicker({ roles, value, onChange, note, disabled, testId = "role-picker" }) {
  return (
    <div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Rolle" data-testid={testId}>
        {(roles || []).map((r) => {
          const on = value === r.key;
          return (
            <button key={r.key} type="button" role="radio" aria-checked={on} disabled={disabled} onClick={() => onChange(r.key)}
              className={cn("group relative rounded-[12px] border px-3.5 py-2.5 text-left transition-[border-color,background-color,box-shadow] duration-150 disabled:opacity-50",
                on ? "border-[hsl(var(--navy))] bg-[hsl(var(--navy)/0.035)] shadow-[0_0_0_1px_hsl(var(--navy))]" : "border-black/[0.09] bg-white hover:border-black/[0.22]")}
              data-testid={`${testId}-${r.key.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center justify-between gap-2">
                <span className={cn("text-[13.5px] font-semibold", on ? "text-[hsl(var(--navy))]" : "text-foreground")}>{r.label}</span>
                <span className={cn("flex h-4 w-4 items-center justify-center rounded-full border transition-colors", on ? "border-[hsl(var(--navy))] bg-[hsl(var(--navy))] text-white" : "border-black/[0.2] bg-white")}>{on ? <Check className="h-2.5 w-2.5" strokeWidth={3.5} /> : null}</span>
              </div>
              <div className="mt-0.5 text-[12px] leading-snug text-muted-foreground">{r.desc}</div>
            </button>
          );
        })}
      </div>
      {note ? <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground" data-testid={`${testId}-note`}>{note}</p> : null}
    </div>
  );
}

/** The explicit admin rights. Off is the normal state; the destructive one says so in words. */
export function FlagList({ flags, value, onChange, lockedKeys = {}, testId = "flag-list" }) {
  return (
    <ul className="divide-y divide-black/[0.05] rounded-[12px] border border-black/[0.07]" data-testid={testId}>
      {(flags || []).map((f) => {
        const lock = lockedKeys[f.key];
        const on = Boolean(value?.[f.key]);
        return (
          <li key={f.key} className="flex items-start gap-3 px-3.5 py-3">
            <div className="min-w-0 flex-1">
              <label htmlFor={`${testId}-${f.key}`} className="flex items-center gap-1.5 text-[13.5px] font-medium">
                {f.label}
                {f.kind === "destruktiv" ? <ShieldAlert className="h-3.5 w-3.5 text-[hsl(var(--v-blocked))]" aria-label="Destruktiv" /> : null}
              </label>
              <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">{f.desc}</p>
              {lock ? <p className="mt-1 text-[11.5px] text-[hsl(var(--v-assumed))]" data-testid={`${testId}-${f.key}-lock`}>{lock}</p> : null}
            </div>
            <Switch id={`${testId}-${f.key}`} checked={on} disabled={Boolean(lock)} onCheckedChange={(v) => onChange({ ...(value || {}), [f.key]: v })} className="mt-0.5 data-[state=checked]:bg-[hsl(var(--navy))]" data-testid={`${testId}-${f.key}`} />
          </li>
        );
      })}
    </ul>
  );
}

export function StateText({ state, className, testId }) {
  if (!state) return null;
  const tone = state.tone === "warn" ? "text-[hsl(var(--v-assumed))]" : state.tone === "bad" ? "text-[hsl(var(--v-blocked))]" : "text-muted-foreground";
  const dot = state.tone === "warn" ? "bg-[hsl(var(--v-assumed))]" : state.tone === "bad" ? "bg-[hsl(var(--v-blocked))]" : "bg-black/25";
  return <span className={cn("inline-flex items-center gap-1.5 text-[12.5px] font-medium", tone, className)} data-testid={testId} data-state={state.key}><span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dot)} />{state.label}</span>;
}
